import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  limit,
  increment,
  deleteField,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../config/firebase";
import {
  MapData,
  Comment,
  Like,
  User,
  FirestoreMapData,
  FirestoreState,
  State,
  Coordinates,
} from "../types";
import { saveMapVersion } from "./versionService";

// Utility function to clean undefined values for Firestore
const cleanForFirestore = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  );
};

// Test Firestore connection
export const testFirestoreConnection = async () => {
  try {
    await getDoc(doc(db, "test", "connection"));
    return true;
  } catch (error) {
    console.error("Firestore connection failed:", error);
    return false;
  }
};

// Test if maps collection exists and has any documents
export const testMapsCollection = async () => {
  try {
    // Try to get all documents (without filters)
    const q = query(collection(db, "maps"), limit(5));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.length;
  } catch (error) {
    console.error("Error testing maps collection:", error);
    return 0;
  }
};

// Convert State to FirestoreState (flatten coordinates)
export const convertStateToFirestore = (state: State): FirestoreState => {
  return {
    ...state,
    coordinates: state.coordinates.flat(), // Flatten the nested array
  };
};

// Convert FirestoreState to State (restore nested coordinates)
export const convertFirestoreToState = (
  firestoreState: FirestoreState
): State => {
  // Ensure coordinates is always an array of arrays
  let coordinates: Coordinates[][];

  if (Array.isArray(firestoreState.coordinates)) {
    // If coordinates is already an array, check if it's nested
    if (
      firestoreState.coordinates.length > 0 &&
      Array.isArray(firestoreState.coordinates[0])
    ) {
      // Already nested format
      coordinates = firestoreState.coordinates as unknown as Coordinates[][];
    } else {
      // Flat array, wrap it
      coordinates = [firestoreState.coordinates as unknown as Coordinates[]];
    }
  } else {
    // Fallback to empty array
    coordinates = [];
  }

  return {
    ...firestoreState,
    coordinates: coordinates,
  };
};

// Convert MapData to FirestoreMapData
const convertMapToFirestore = (
  mapData: Omit<
    MapData,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "likesCount"
    | "commentsCount"
    | "viewsCount"
  >
): Omit<
  FirestoreMapData,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "likesCount"
  | "commentsCount"
  | "viewsCount"
> => {
  // Clean undefined values for Firestore
  const cleanData = Object.fromEntries(
    Object.entries(mapData).filter(([_, value]) => value !== undefined)
  );

  const firestoreData: any = {
    ...cleanData,
    states: mapData.states.map(convertStateToFirestore),
    guideLayers: mapData.guideLayers || [],
  };

  // Convert customBorder to JSON string (Firestore doesn't support nested arrays)
  if (mapData.customBorder) {
    firestoreData.customBorderJson = JSON.stringify(mapData.customBorder);
    delete firestoreData.customBorder; // Remove the nested array version
  }

  return firestoreData as Omit<
    FirestoreMapData,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "likesCount"
    | "commentsCount"
    | "viewsCount"
  >;
};

// Convert FirestoreMapData to MapData
const convertFirestoreToMap = (firestoreMap: FirestoreMapData): MapData => {
  const mapData: any = {
    ...firestoreMap,
    states: firestoreMap.states.map(convertFirestoreToState),
    guideLayers: firestoreMap.guideLayers || [],
  };

  // Restore customBorder from JSON string
  if (firestoreMap.customBorderJson) {
    mapData.customBorder = JSON.parse(firestoreMap.customBorderJson);
    delete mapData.customBorderJson; // Remove the JSON string version
  }

  return mapData as MapData;
};

// Move thumbnail from temporary location to final location
export const moveThumbnailToFinalLocation = async (
  tempMapId: string,
  finalMapId: string
) => {
  try {
    // Get the temporary thumbnail URL
    const tempStorageRef = ref(storage, `map-thumbnails/${tempMapId}`);
    const tempUrl = await getDownloadURL(tempStorageRef);

    // Download the file from temporary location
    const response = await fetch(tempUrl);
    const blob = await response.blob();

    // Upload to final location
    const finalStorageRef = ref(storage, `map-thumbnails/${finalMapId}`);
    await uploadBytes(finalStorageRef, blob);
    const finalUrl = await getDownloadURL(finalStorageRef);

    // Delete the temporary file
    // Note: Firebase Storage doesn't have a direct delete method in the client SDK
    // The temporary files will be cleaned up by a server-side process or manually

    return finalUrl;
  } catch (error) {
    console.error("Error moving thumbnail:", error);
    throw error;
  }
};

export const createMap = async (
  mapData: Omit<
    MapData,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "likesCount"
    | "commentsCount"
    | "viewsCount"
  >
) => {
  try {
    // Convert to Firestore-compatible format
    const firestoreMapData = convertMapToFirestore(mapData);

    const mapWithMetadata: Omit<FirestoreMapData, "id"> = {
      ...firestoreMapData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      likesCount: 0,
      commentsCount: 0,
      viewsCount: 0,
      version: 1, // Initialize version to 1 for new maps
    };

    const docRef = await addDoc(collection(db, "maps"), mapWithMetadata);
    const createdFirestoreMap = { id: docRef.id, ...mapWithMetadata };

    // Handle thumbnail if it's a temporary URL
    if (mapData.thumbnail && mapData.thumbnail.includes("temp-")) {
      // Extract the temporary map ID from the thumbnail URL
      const tempMapId = mapData.thumbnail.split("/").pop()?.split("?")[0];
      if (tempMapId && tempMapId.startsWith("temp-")) {
        try {
          // Move thumbnail to final location
          const finalThumbnailUrl = await moveThumbnailToFinalLocation(
            tempMapId,
            docRef.id
          );

          // Update the map with the final thumbnail URL
          await updateDoc(docRef, {
            thumbnail: finalThumbnailUrl,
          });

          // Update the created map data
          createdFirestoreMap.thumbnail = finalThumbnailUrl;
        } catch (error) {
          console.error("Error moving thumbnail to final location:", error);
          // Continue without thumbnail if move fails
        }
      }
    }

    // Increment user's maps count
    const userRef = doc(db, "users", mapData.userId);
    await updateDoc(userRef, {
      mapsCount: increment(1),
    });

    // Save initial version snapshot (v1) for the newly created map
    // Use the final thumbnail URL (after potential move from temp location)
    try {
      await saveMapVersion(
        docRef.id,
        {
          name: mapData.name,
          description: mapData.description,
          states: mapData.states || [],
          guideLayers: mapData.guideLayers || [],
          version: 1,
          userId: mapData.userId,
          userDisplayName: mapData.userDisplayName,
          thumbnail: createdFirestoreMap.thumbnail || mapData.thumbnail,
          isApproved: false,
          approvedAt: undefined,
          approvedBy: undefined,
        },
        "Initial version"
      );
    } catch (versionError) {
      console.error("Error saving initial version snapshot:", versionError);
      // Continue even if version save fails - map creation should still succeed
    }

    // Convert back to regular MapData format
    const createdMap = convertFirestoreToMap(createdFirestoreMap);
    return createdMap;
  } catch (error) {
    console.error("Error creating map:", error);
    throw error;
  }
};

export const updateMap = async (mapId: string, updates: Partial<MapData>) => {
  try {
    const mapRef = doc(db, "maps", mapId);

    // Get current map to check if content is being modified
    const currentMapDoc = await getDoc(mapRef);
    const currentMap = currentMapDoc.exists() ? currentMapDoc.data() : null;

    // Convert states to Firestore format if they exist in updates
    let updateData: any = {
      updatedAt: new Date().toISOString(),
    };

    // Check if map content (states, guideLayers, name, or description) is actually modified
    // We need to compare the values, not just check if they're defined
    let isContentModified = false;

    if (currentMap) {
      // Compare states (deep comparison)
      // Note: We compare content only, not IDs, since IDs may be regenerated
      if (updates.states !== undefined) {
        const currentStates = (currentMap.states || []).map(
          convertFirestoreToState
        );
        // Normalize states for comparison (sort by name to ensure consistent comparison)
        const currentStatesNormalized = currentStates
          .map((s: State) => ({
            name: s.name,
            coordinates: JSON.stringify(s.coordinates),
            color: s.color,
            capital: s.capital,
          }))
          .sort((a: { name: string }, b: { name: string }) =>
            a.name.localeCompare(b.name)
          );
        const newStatesNormalized = updates.states
          .map((s: State) => ({
            name: s.name,
            coordinates: JSON.stringify(s.coordinates),
            color: s.color,
            capital: s.capital,
          }))
          .sort((a: { name: string }, b: { name: string }) =>
            a.name.localeCompare(b.name)
          );
        if (
          JSON.stringify(currentStatesNormalized) !==
          JSON.stringify(newStatesNormalized)
        ) {
          isContentModified = true;
        }
      }

      // Compare guideLayers
      if (!isContentModified && updates.guideLayers !== undefined) {
        const currentGuideLayers = JSON.stringify(currentMap.guideLayers || []);
        const newGuideLayers = JSON.stringify(updates.guideLayers || []);
        if (currentGuideLayers !== newGuideLayers) {
          isContentModified = true;
        }
      }

      // Compare name
      if (!isContentModified && updates.name !== undefined) {
        if (currentMap.name !== updates.name) {
          isContentModified = true;
        }
      }

      // Compare description
      if (!isContentModified && updates.description !== undefined) {
        if ((currentMap.description || "") !== (updates.description || "")) {
          isContentModified = true;
        }
      }
    }

    // If content is modified, save version snapshot and reset approval status
    if (isContentModified && currentMap) {
      const currentVersion = (currentMap.version || 0) as number;
      const newVersion = currentVersion + 1;

      // Note: We don't save the current version snapshot here because it should already exist
      // from the previous update cycle (or from initial creation for v1).
      // We only save the NEW version snapshot after the update completes.

      updateData.version = newVersion;

      // After updating, save a snapshot of the NEW state with the new version number
      // This ensures the preview shows the correct state for the new version
      // We'll do this after the update completes
      // Reset approval status when content changes
      updateData.isApproved = false;
      updateData.approvalRequested = false;
      updateData.isRejected = false;
      // Remove rejectionReason field using deleteField() for Firestore
      updateData.rejectionReason = deleteField();
      // Keep track of which version was approved (if any)
      if (currentMap.isApproved && currentMap.version) {
        updateData.approvedVersion = currentMap.version;
      }
    }

    // Only include fields that have valid values (not undefined)
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && key !== "customBorder") {
        // Skip customBorder here, we'll handle it separately
        updateData[key] = value;
      }
    });

    // If states are being updated, convert them to Firestore format
    if (updates.states) {
      updateData.states = updates.states.map(convertStateToFirestore);
    }

    // If guideLayers are being updated, ensure they're in the right format
    if (updates.guideLayers) {
      updateData.guideLayers = updates.guideLayers;
    }

    // If customBorder is being updated, convert it to JSON string
    if (updates.customBorder !== undefined) {
      if (updates.customBorder) {
        updateData.customBorderJson = JSON.stringify(updates.customBorder);
      } else {
        // If customBorder is null/undefined, remove it from Firestore
        updateData.customBorderJson = deleteField();
      }
    }

    await updateDoc(mapRef, updateData);

    // After updating, save a snapshot of the NEW state with the new version number
    // This ensures previews show the correct state for each version
    if (isContentModified && updateData.version && currentMap) {
      try {
        // Get the updated map to save as the new version snapshot
        const updatedMapDoc = await getDoc(mapRef);
        const updatedMap = updatedMapDoc.exists() ? updatedMapDoc.data() : null;

        if (updatedMap) {
          const newStates = (updatedMap.states || []).map(
            convertFirestoreToState
          );
          const newVersion = updateData.version as number;
          const previousVersion = (currentMap.version || 0) as number;

          // Save the NEW state as a snapshot for the new version
          await saveMapVersion(
            mapId,
            {
              name: updatedMap.name || "",
              description: updatedMap.description,
              states: newStates,
              guideLayers: updatedMap.guideLayers || [],
              version: newVersion,
              userId: updatedMap.userId || "",
              userDisplayName: updatedMap.userDisplayName || "",
              thumbnail: updatedMap.thumbnail,
              isApproved: updatedMap.isApproved,
              approvedAt: updatedMap.approvedAt,
              approvedBy: updatedMap.approvedBy,
            },
            `Version ${previousVersion} → ${newVersion}`
          );
        }
      } catch (versionError) {
        console.error("Error saving new version snapshot:", versionError);
        // Don't fail the update if version save fails
      }
    }
  } catch (error) {
    console.error("Error updating map:", error);
    console.error("Error details:", {
      message: error instanceof Error ? error.message : "Unknown error",
      code: (error as any)?.code,
      stack: error instanceof Error ? error.stack : undefined,
    });
    throw error;
  }
};

export const deleteMap = async (mapId: string) => {
  try {
    // Get the map data first to get the userId
    const mapData = await getMapById(mapId);
    if (!mapData) {
      throw new Error("Map not found");
    }

    // Delete the map
    const mapRef = doc(db, "maps", mapId);
    await deleteDoc(mapRef);

    // Decrement user's maps count
    const userRef = doc(db, "users", mapData.userId);
    await updateDoc(userRef, {
      mapsCount: increment(-1),
    });
  } catch (error) {
    console.error("Error deleting map:", error);
    throw error;
  }
};

export const getPublicMaps = async (limitCount: number = 20) => {
  try {
    const q = query(
      collection(db, "maps"),
      where("isPublic", "==", true),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.docs.length === 0) {
      return [];
    }

    // Process maps more efficiently
    const firestoreMaps = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
      } as FirestoreMapData;
    });

    // Sort in memory (newest first) - only if we have multiple maps
    if (firestoreMaps.length > 1) {
      firestoreMaps.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }

    // Convert maps synchronously for better performance
    const maps = firestoreMaps.map((firestoreMap) => {
      try {
        const converted = convertFirestoreToMap(firestoreMap);
        return converted;
      } catch (conversionError) {
        console.error(
          `❌ Error converting map ${firestoreMap.name}:`,
          conversionError
        );
        // Return a simplified version if conversion fails
        return {
          ...firestoreMap,
          states: [],
        } as MapData;
      }
    });

    return maps;
  } catch (error) {
    console.error("Error loading maps:", error);
    throw error;
  }
};

export const getUserMaps = async (userId: string, limitCount?: number) => {
  try {
    // Temporary: Remove orderBy to avoid composite index requirement
    let q = query(
      collection(db, "maps"),
      where("userId", "==", userId)
      // orderBy("createdAt", "desc") // Temporarily removed
    );

    // Add limit if specified (default: no limit for backward compatibility)
    // But we'll apply a reasonable max limit server-side to prevent abuse
    // 10,000 maps should be more than enough for any legitimate user
    // This is high enough for tests but prevents loading millions of maps
    const MAX_MAPS_LIMIT = 10000;
    if (limitCount !== undefined) {
      q = query(q, limit(Math.min(limitCount, MAX_MAPS_LIMIT)));
    } else {
      // Apply default safety limit if no limit specified
      q = query(q, limit(MAX_MAPS_LIMIT));
    }

    const querySnapshot = await getDocs(q);

    const maps = querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as MapData)
    );

    // Sort in memory (newest first)
    maps.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return maps;
  } catch (error) {
    console.error("Error fetching user maps:", error);
    throw error;
  }
};

export const getMapById = async (mapId: string) => {
  try {
    const mapDoc = await getDoc(doc(db, "maps", mapId));
    if (mapDoc.exists()) {
      const firestoreData = {
        id: mapDoc.id,
        ...mapDoc.data(),
      } as FirestoreMapData;

      // Convert Firestore data to regular MapData format
      const convertedMap = convertFirestoreToMap(firestoreData);

      return convertedMap;
    }
    return null;
  } catch (error) {
    console.error("Error fetching map:", error);
    throw error;
  }
};

// Like operations
export const likeMap = async (mapId: string, userId: string) => {
  try {
    // Check if already liked
    const likeQuery = query(
      collection(db, "likes"),
      where("mapId", "==", mapId),
      where("userId", "==", userId)
    );

    const existingLikes = await getDocs(likeQuery);

    if (!existingLikes.empty) {
      return; // Already liked
    }

    // Add like
    const likeData = {
      mapId,
      userId,
      createdAt: new Date().toISOString(),
    };

    // Filter out undefined values for Firestore
    const cleanLikeData = cleanForFirestore(likeData);

    await addDoc(collection(db, "likes"), cleanLikeData);

    // Increment likes count
    await updateDoc(doc(db, "maps", mapId), {
      likesCount: increment(1),
    });
  } catch (error) {
    console.error("Error liking map:", error);
    throw error;
  }
};

export const unlikeMap = async (mapId: string, userId: string) => {
  try {
    const likeQuery = query(
      collection(db, "likes"),
      where("mapId", "==", mapId),
      where("userId", "==", userId)
    );

    const existingLikes = await getDocs(likeQuery);
    if (!existingLikes.empty) {
      await deleteDoc(existingLikes.docs[0].ref);

      // Decrement likes count
      await updateDoc(doc(db, "maps", mapId), {
        likesCount: increment(-1),
      });
    }
  } catch (error) {
    console.error("Error unliking map:", error);
    throw error;
  }
};

export const getMapLikes = async (mapId: string) => {
  try {
    const q = query(collection(db, "likes"), where("mapId", "==", mapId));

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as Like)
    );
  } catch (error) {
    console.error("Error fetching map likes:", error);
    throw error;
  }
};

export const isMapLikedByUser = async (mapId: string, userId: string) => {
  try {
    const q = query(
      collection(db, "likes"),
      where("mapId", "==", mapId),
      where("userId", "==", userId)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking if map is liked:", error);
    return false;
  }
};

// Comment operations
export const addComment = async (
  mapId: string,
  userId: string,
  userDisplayName: string,
  userAvatar: string | undefined,
  content: string
) => {
  try {
    // Create comment data and filter out undefined values
    const commentData = {
      mapId,
      userId,
      userDisplayName,
      userAvatar,
      content,
      createdAt: new Date().toISOString(),
    };

    // Filter out undefined values for Firestore
    const cleanCommentData = cleanForFirestore(commentData);

    await addDoc(collection(db, "comments"), cleanCommentData);

    // Increment comments count
    const mapRef = doc(db, "maps", mapId);
    await updateDoc(mapRef, {
      commentsCount: increment(1),
    });
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

export const getMapComments = async (mapId: string) => {
  try {
    // Temporary: Remove orderBy to avoid composite index requirement
    const q = query(
      collection(db, "comments"),
      where("mapId", "==", mapId)
      // orderBy("createdAt", "desc") // Temporarily removed
    );

    const querySnapshot = await getDocs(q);

    const comments = querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as Comment)
    );

    // Sort in memory (newest first)
    comments.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return comments;
  } catch (error) {
    console.error("Error fetching map comments:", error);
    throw error;
  }
};

export const deleteComment = async (commentId: string, mapId: string) => {
  try {
    await deleteDoc(doc(db, "comments", commentId));

    // Decrement comments count
    await updateDoc(doc(db, "maps", mapId), {
      commentsCount: increment(-1),
    });
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};

// User operations
export const getUserById = async (userId: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

// Upload thumbnail
export const uploadMapThumbnail = async (mapId: string, file: File) => {
  try {
    const storageRef = ref(storage, `map-thumbnails/${mapId}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    // Only update the map document if it exists (not a temporary ID)
    if (!mapId.startsWith("temp-")) {
      await updateDoc(doc(db, "maps", mapId), {
        thumbnail: downloadURL,
      });
    }

    return downloadURL;
  } catch (error) {
    console.error("Error uploading thumbnail:", error);
    throw error;
  }
};

// Request map approval
export const requestMapApproval = async (mapId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, "maps", mapId), {
      approvalRequested: true,
      approvalRequestedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error requesting map approval:", error);
    throw error;
  }
};

// Create a test map for debugging
export const createTestMap = async () => {
  try {
    const testMapData = {
      name: "Test Map",
      description: "This is a test map for debugging",
      states: [
        {
          id: "test-state-1",
          name: "Test State",
          coordinates: [
            [
              { lat: 35.6892, lng: 51.389 },
              { lat: 35.6892, lng: 51.489 },
              { lat: 35.7892, lng: 51.489 },
              { lat: 35.7892, lng: 51.389 },
            ],
          ],
          color: "#ff0000",
          capital: "Test Capital",
          population: 1000000,
          createdAt: new Date().toISOString(),
        },
      ],
      country: "Iran",
      userId: "test-user",
      userDisplayName: "Test User",
      isPublic: true,
      tags: ["test"],
    };

    const createdMap = await createMap(testMapData);
    return createdMap;
  } catch (error) {
    console.error("Error creating test map:", error);
    throw error;
  }
};
